import React, { useState, useEffect, useCallback } from "react";
import Modal from "react-modal";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { withRouter } from "react-router";
import { useTranslation } from "react-i18next";
import LoadingIcon from "mdi-react/LoadingIcon";

import { globalSystemConstants, reelsConstants } from "../../constants";
import { customToast } from "../../shared";
import { reelsActions } from "../../redux/actions";

const ReelTypes = ({
  recapTypes,
  spinnerActions,
  getRecapTypes,
  updateRecapType,
  activateDownloadSpinner,
  diactivateDownloadSpinner,
}) => {
  const { t } = useTranslation("common");

  const [recapTypeModalIsOpen, setRecapTypeModalIsOpen] = useState(false);
  const [recapTypeVideoUrl, setRecapTypeVideoUrl] = useState("");
  const [recapFormData, setRecapFormData] = useState([]);

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((f) => f === type),
    [spinnerActions]
  );

  useEffect(() => {
    getRecapTypes();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (recapTypes?.length > 0) {
      setRecapFormData(recapTypes);
    } else {
      setRecapFormData([]);
    }
  }, [recapTypes]);

  const handleRecapTypeInputChange = (e, id) => {
    const { name, value } = e.target;

    setRecapFormData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, [name]: value } : item
      )
    );
  };

  const handleRecapTypeFileChange = (e, id) => {
    const { name, files } = e.target;

    setRecapFormData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, [name]: files[0] } : item
      )
    );
  };

  const handleCloseModal = () => {
    setRecapTypeModalIsOpen(false);
    setRecapTypeVideoUrl(null);
  };

  const handleSubmitRecapType = (e) => {
    e.preventDefault();

    for (const recap of recapFormData) {
      const priceNumber = Number(recap.price);

      // Validate price
      if (isNaN(priceNumber) || priceNumber <= 0) {
        customToast(t("price_must_be_positive_number"), toast.TYPE.ERROR);
        return;
      }

      // Validate File
      if (recap.sampleFile && !recap.sampleFile.type.startsWith("video/")) {
        customToast(t("invalid_sample_file_type"), toast.TYPE.ERROR);
        return;
      }
    }

    // FormData
    const formDataToSend = new FormData();
    recapFormData.forEach((recap, index) => {
      formDataToSend.append(`dto[${index}].id`, recap.id);
      formDataToSend.append(`dto[${index}].name`, recap.name);
      formDataToSend.append(`dto[${index}].price`, recap.price);

      if (recap.sampleFile) {
        formDataToSend.append(`${recap.name}`, recap.sampleFile);
      }
    });

    updateRecapType(formDataToSend);
  };

  const handleSingleDownload = async (file) => {
    activateDownloadSpinner();
    try {
      const fileExtenssion = file?.split(".").pop();
      const fileName = file.match(/\/([^\/]+?)(\.[^\/.]+)?$/)[1];
      const fileUrl = file !== "undefined" ? file : "";
      let modifiedBlob;

      const randomNumber = Math.floor(Math.random() * 100) + 1;

      const response = await fetch(fileUrl);
      modifiedBlob = await response.blob();

      const url = window.URL.createObjectURL(modifiedBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        fileName !== "undefined"
          ? `${fileName}.${fileExtenssion}`
          : randomNumber;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the temporary URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
    diactivateDownloadSpinner();
  };

  return (
    <>
      <div className="row mr-0">
        <div className="col-md-12 recap-type-container">
          <div className="col-12 recap-type-box">
            <h5>Recap Type</h5>

            {spinner(reelsConstants.GET_RECAP_TYPE_DETAILS) ? (
              <>
                <form onSubmit={handleSubmitRecapType}>
                  <div className="row w-100 mt-4">
                    {recapFormData?.map((recapType) => {
                      return (
                        <div key={recapType.id} className="col-6">
                          <p className="fw-bold pb-0">{recapType.name}</p>

                          <div className="col-12 col-md-6 p-2">
                            <label>Price</label>
                            <input
                              type="number"
                              name="price"
                              className="form-control"
                              value={recapType.price}
                              onChange={(e) =>
                                handleRecapTypeInputChange(e, recapType.id)
                              }
                            />
                          </div>
                          <div className="col-12 col-md-6 p-2">
                            <label>{`Sample ${recapType.name}`}</label>
                            <input
                              type="file"
                              name="sampleFile"
                              className="form-control"
                              onChange={(e) =>
                                handleRecapTypeFileChange(e, recapType.id)
                              }
                              accept="video/*"
                            />
                          </div>
                          <div className="col-12 col-md-6 pt-2">
                            <button
                              type="button"
                              onClick={() => {
                                if (recapType.url) {
                                  setRecapTypeVideoUrl(
                                    `${
                                      process.env
                                        .REACT_APP_S3_BASE_URL_EVENTS_GALLERY +
                                      "/" +
                                      recapType.url
                                    }`
                                  );
                                } else {
                                  console.error(
                                    "No video available for preview"
                                  );
                                  return;
                                }
                                setRecapTypeModalIsOpen(true);
                              }}
                              className="recap-type-view-sample"
                            >
                              view sample
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <div className="col-12 text-right">
                      <button
                        type="submit"
                        className="btn btn-primary mb-0"
                        disabled={
                          recapFormData.length === 0 ||
                          !spinner(reelsConstants.UPDATE_RECAP_TYPE)
                        }
                      >
                        {!spinner(reelsConstants.UPDATE_RECAP_TYPE) ? (
                          <LoadingIcon className="loading-spinner" />
                        ) : (
                          t("update")
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center mt-4">
                <LoadingIcon className="loading-spinner" />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={recapTypeModalIsOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Image Modal"
        className="image-modal"
      >
        <div className="modaal-content">
          <>
            <div
              className={"video-download-icon"}
              onClick={() => handleSingleDownload(recapTypeVideoUrl)}
            >
              <i className="fas fa-download text-light"></i>
            </div>
            <video
              src={recapTypeVideoUrl}
              className="modal-image"
              controls
            ></video>
          </>

          <button onClick={handleCloseModal} className="gallery-modal-close">
            <img
              src="/images/simpleCrossWhite.svg"
              alt="crossIcon"
              className="modal-close-img"
            />
          </button>
        </div>
      </Modal>
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  recapTypes: state.reels.recapTypes,
  spinnerActions: state.system.spinnerActions,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getRecapTypes: () => dispatch(reelsActions.getRecapTypes()),
  updateRecapType: (data) => dispatch(reelsActions.updateRecapType(data)),
  activateDownloadSpinner: () =>
    dispatch({
      type: globalSystemConstants.DOWNLOAD_SPINNER_ACTIVATE,
      data: null,
    }),
  diactivateDownloadSpinner: () =>
    dispatch({
      type: globalSystemConstants.DOWNLOAD_SPINNER_DIACTIVATE,
      data: null,
    }),
});

// Mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ReelTypes));
